import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getAppUser,
  listScans,
  updateAppUserProfile,
  updateAppUserStatus,
  type AdminScanSummary,
  type AppUser,
  type AppUserProfileUpdate,
  type AppUserStatus,
} from '../lib/api';
import { StatusBadge } from './AppUsersPage';
import { theme } from '../theme';

export function AppUserDetailPage() {
  const { userId = '' } = useParams<{ userId: string }>();
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit-form local state. Initialised from the fetched user; PATCH payload
  // only includes fields the user touched (we diff against the loaded user).
  const [edit, setEdit] = useState<AppUserProfileUpdate>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    getAppUser(userId)
      .then((res) => {
        setUser(res.user);
        setEdit({});
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleField = (field: keyof AppUserProfileUpdate, rawValue: string): void => {
    // Treat empty string as "clear field" (null). Cognito treats empty custom
    // attributes as unset; keep the wire format explicit so the backend
    // distinguishes "leave as-is" (omitted) from "clear" (null).
    setEdit((prev) => ({
      ...prev,
      [field]: rawValue === '' ? null : rawValue,
    }));
  };

  const handleBoolField = (field: keyof AppUserProfileUpdate, value: boolean): void => {
    setEdit((prev) => ({ ...prev, [field]: value }));
  };

  const save = async (): Promise<void> => {
    if (!user) return;
    setSaving(true);
    try {
      const res = await updateAppUserProfile(user.userId, edit);
      setUser(res.user);
      setEdit({});
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status: AppUserStatus): Promise<void> => {
    if (!user) return;
    setSaving(true);
    try {
      const res = await updateAppUserStatus(user.userId, status);
      setUser(res.user);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading…</p>;
  if (error && !user) return <p style={{ color: theme.status.error }}>Error: {error}</p>;
  if (!user) return <p>User not found.</p>;

  const fieldValue = (
    field: keyof AppUserProfileUpdate,
    fallback: string | null | undefined,
  ): string => {
    if (field in edit) {
      const v = edit[field];
      return v === null ? '' : String(v ?? '');
    }
    return fallback ?? '';
  };

  const dirty = Object.keys(edit).length > 0;

  return (
    <div>
      <Link to="/app-users" style={{ fontSize: 13, color: theme.text.secondary }}>
        ← All app users
      </Link>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginTop: 12 }}>
        <h1 style={{ margin: 0 }}>
          {user.firstName || user.lastName
            ? `${user.firstName} ${user.lastName}`.trim()
            : user.phoneE164}
        </h1>
        <StatusBadge status={user.status} />
      </div>
      <p style={{ color: theme.text.secondary, fontSize: 13, fontFamily: theme.fonts.mono }}>
        {user.phoneE164} · userId {user.userId}
      </p>
      {error && <p style={{ color: theme.status.error }}>Error: {error}</p>}

      <section style={sectionStyle}>
        <h2 style={h2Style}>Status</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {user.status !== 'active' && (
            <ActionButton
              variant="primary"
              onClick={() => void changeStatus('active')}
              disabled={saving}
            >
              {user.status === 'pending-approval' ? 'Approve' : 'Re-enable'}
            </ActionButton>
          )}
          {user.status !== 'disabled' && (
            <ActionButton
              variant="danger"
              onClick={() => void changeStatus('disabled')}
              disabled={saving}
            >
              Disable
            </ActionButton>
          )}
          {user.status === 'active' && (
            <ActionButton
              variant="neutral"
              onClick={() => void changeStatus('pending-approval')}
              disabled={saving}
            >
              Move back to pending
            </ActionButton>
          )}
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Profile</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <label>First name</label>
          <input
            value={fieldValue('firstName', user.firstName)}
            onChange={(e) => handleField('firstName', e.target.value)}
          />
          <label>Last name</label>
          <input
            value={fieldValue('lastName', user.lastName)}
            onChange={(e) => handleField('lastName', e.target.value)}
          />
          <label>Rooftop</label>
          <input
            value={fieldValue('rooftopName', user.rooftopName)}
            onChange={(e) => handleField('rooftopName', e.target.value)}
            placeholder="e.g. Dublin Branch"
          />
          <label>TradeBid user</label>
          <input
            value={fieldValue('associatedTradeBidUser', user.associatedTradeBidUser)}
            onChange={(e) => handleField('associatedTradeBidUser', e.target.value)}
            placeholder="TradeBid user id (cross-link)"
          />
          <label>Tramovo user</label>
          <input
            value={fieldValue('associatedTramovoUser', user.associatedTramovoUser)}
            onChange={(e) => handleField('associatedTramovoUser', e.target.value)}
            placeholder="Tramovo user id (cross-link)"
          />
          <label>Debug scan mode</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={
                'debugScanMode' in edit ? Boolean(edit.debugScanMode) : Boolean(user.debugScanMode)
              }
              onChange={(e) => handleBoolField('debugScanMode', e.target.checked)}
            />
            <span style={{ color: theme.text.secondary }}>
              Run experimental OBDb commands (dbgfilter) during this user's scans. Collects
              field-capture evidence for the local profile-promotion pipeline. Scans take longer;
              results never appear in the user-facing report.
            </span>
          </label>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <ActionButton variant="primary" onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </ActionButton>
          {dirty && (
            <ActionButton variant="neutral" onClick={() => setEdit({})} disabled={saving}>
              Cancel
            </ActionButton>
          )}
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Metadata</h2>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 16px',
            fontSize: 13,
          }}
        >
          <dt style={{ color: theme.text.secondary }}>Joined</dt>
          <dd style={{ margin: 0 }}>{new Date(user.createdAt).toLocaleString()}</dd>
        </dl>
      </section>

      <RecentScansSection userId={user.userId} />
    </div>
  );
}

function RecentScansSection({ userId }: { userId: string }) {
  const [scans, setScans] = useState<AdminScanSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setScans(null);
    setError(null);
    listScans({ userId, limit: 50 })
      .then((res) => setScans(res.scans))
      .catch((err: Error) => setError(err.message));
  }, [userId]);

  return (
    <section style={sectionStyle}>
      <h2 style={h2Style}>Recent scans</h2>
      {error && <p style={{ color: theme.status.error, fontSize: 13 }}>Error: {error}</p>}
      {!error && scans === null && <p style={{ fontSize: 13 }}>Loading…</p>}
      {scans && scans.length === 0 && (
        <p style={{ color: theme.text.secondary, fontSize: 13 }}>
          This user hasn&apos;t uploaded any scans yet.
        </p>
      )}
      {scans && scans.length > 0 && (
        <table style={{ width: '100%' }}>
          <thead>
            <tr style={{ background: theme.surface.header }}>
              <UserScanTh>Scanned</UserScanTh>
              <UserScanTh>ID</UserScanTh>
              <UserScanTh>Vehicle</UserScanTh>
              <UserScanTh>VIN</UserScanTh>
              <UserScanTh>Outcome</UserScanTh>
              <UserScanTh>DTCs</UserScanTh>
              <UserScanTh>Actions</UserScanTh>
            </tr>
          </thead>
          <tbody>
            {scans.map((s) => (
              <tr key={s.scanId} style={{ borderBottom: `1px solid ${theme.surface.border}` }}>
                <UserScanTd>
                  <time>{new Date(s.scannedAt).toLocaleString()}</time>
                </UserScanTd>
                <UserScanTd style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>
                  {s.scanShortcodeBody ? `OB-${s.scanShortcodeBody}` : `${s.scanId.slice(0, 8)}…`}
                </UserScanTd>
                <UserScanTd>
                  {s.make ?? '—'} {s.model ?? ''} {s.modelYear ?? ''}
                </UserScanTd>
                <UserScanTd style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>
                  {s.vin ?? '—'}
                </UserScanTd>
                <UserScanTd>
                  <span
                    style={{
                      color: s.outcome === 'success' ? theme.status.success : theme.status.error,
                      fontWeight: 600,
                    }}
                  >
                    {s.outcome}
                  </span>
                </UserScanTd>
                <UserScanTd>{s.totalDtcs ?? '—'}</UserScanTd>
                <UserScanTd>
                  <Link to={`/scans/${s.scanId}`}>Details</Link>
                  {s.reportUrl && (
                    <>
                      {' · '}
                      <a href={s.reportUrl} target="_blank" rel="noreferrer">
                        PDF
                      </a>
                    </>
                  )}
                  {s.batteryReportUrl && (
                    <>
                      {' · '}
                      <a href={s.batteryReportUrl} target="_blank" rel="noreferrer">
                        Battery PDF
                      </a>
                    </>
                  )}
                </UserScanTd>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function UserScanTh({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        color: theme.text.secondary,
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}

function UserScanTd({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return <td style={{ padding: '8px 12px', fontSize: 13, ...style }}>{children}</td>;
}

function ActionButton({
  variant,
  onClick,
  disabled,
  children,
}: {
  variant: 'primary' | 'danger' | 'neutral';
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const styles: Record<typeof variant, React.CSSProperties> = {
    primary: { background: theme.brand.primary, color: theme.text.onBrand, fontWeight: 600 },
    danger: {
      background: 'transparent',
      color: theme.status.error,
      border: `1px solid ${theme.status.errorBorder}`,
    },
    neutral: {
      background: theme.surface.panel,
      color: theme.text.primary,
      border: `1px solid ${theme.surface.borderStrong}`,
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 14px',
        borderRadius: theme.radius.md,
        fontSize: 13,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        border: 'none',
        ...styles[variant],
      }}
    >
      {children}
    </button>
  );
}

const sectionStyle: React.CSSProperties = {
  marginTop: 24,
  padding: 16,
  border: `1px solid ${theme.surface.border}`,
  borderRadius: theme.radius.lg,
  background: theme.surface.panel,
};

const h2Style: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: theme.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginTop: 0,
  marginBottom: 12,
};
